import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { getPath, getTextContrast } from '../../../utils';
import { GametypeWave } from '../../../../assets/icons/gametype-wave';
import './user-score.m.css';

async function getAccessToken(bridgeKeeperIntegrationId, sketchesHost) {
  const accessTokenUrl = getPath({
    sketchesHost,
    relativePath: `/api/auth/v1/access-token/integrations/${bridgeKeeperIntegrationId}`
  });
  const response = await fetch(accessTokenUrl);
  return response.headers.get('x-integration-token');
}

export function UserScore({ isDarkModeEnabled }) {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  const gametypeOpts = useSelector((state) =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'story', 'globalAiElements', 'quiz', 'opts'], {})
  );
  const general = useSelector((state) =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'general'], {})
  );
  const sketchesHost = useSelector((state) =>
    get(state, ['qt', 'config', 'sketches-host'], '')
  );

  const {
    enabled,
    gameTypeKey,
    gameTypeMode,
    quizStatsLabel,
    profileTotalPointsLabel
  } = gametypeOpts;
  const bridgeKeeperIntegrationId = get(general, ['accesstypeIntegration', 'bridgeKeeperIntegrationId'], '');

  const { darkPrimaryColor = '#4860BC', primaryColor = '#4860BC' } = general;
  const bgColor = isDarkModeEnabled ? darkPrimaryColor : primaryColor;
  const textColor = getTextContrast(bgColor) === 'dark' ? '#000' : '#fff';

  const gameTypeEnabled = enabled && gameTypeKey;
  const gameTypeHost =
    gameTypeMode === 'staging' ? 'https://gametype.staging.quintype.com' : 'https://gametype.quintype.com';

  useEffect(() => {
    if (!gameTypeEnabled) return;

    let isMounted = true;

    async function fetchProgress() {
      try {
        setLoading(true);
        const accessToken = await getAccessToken(bridgeKeeperIntegrationId, sketchesHost);
        const res = await fetch(`${gameTypeHost}/api/v1/user/progress?publisher_id=${gameTypeKey}`, {
          headers: { Authorization: accessToken }
        });

        if (!res.ok) return;

        const data = await res.json();
        if (isMounted) {
          setScore(data?.total_points ?? null);
        }
      } catch (err) {
        console.error('progress fetch error', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchProgress();

    return () => {
      isMounted = false;
    };
  }, [gameTypeEnabled, bridgeKeeperIntegrationId, sketchesHost, gameTypeHost, gameTypeKey]);

  if (!gameTypeEnabled || loading || !score) return null;

  return (
    <div
      data-test-id='profile-user-score'
      styleName='user-score-container'
      style={{
        backgroundColor: bgColor,
        color: textColor
      }}
    >
      <GametypeWave />
      <h3 styleName='user-score-title'>{quizStatsLabel || 'Quiz Stats'}</h3>
      <span styleName='user-score-value'>{score}</span>
      <span styleName='user-score-label'>{profileTotalPointsLabel || 'Total Score'}</span>
    </div>
  );
}

UserScore.propTypes = {
  isDarkModeEnabled: PropTypes.bool
};
